import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { Layout } from "@/components/Layout";
import { canonicalGameLinkParts, gamePaths, resultPathForStoredSlug } from "@/lib/game-routing";
import { SEOHead } from "@/components/SEOHead";
import { Card, CardContent } from "@/components/ui/card";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { NumberRow } from "@/components/NumberPill";
import { GameLogo } from "@/components/GameLogo";
import { useLatestResults } from "@/hooks/use-query-hooks";
import { CalendarDays, ChevronRight } from "lucide-react";
import { DrawJsonLd } from "@/components/DrawJsonLd";

export const Route = createFileRoute("/results/")({
  head: () => buildSeo({
    title: "Lottery Results — Today's Winning Numbers",
    description: "View today's US lottery results and winning numbers across every state and multi-state game.",
    path: "/results",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "US Lottery Results and Winning Numbers",
      description:
        "Latest US lottery winning numbers, updated daily, for Powerball, Mega Millions, Cash4Life, and every state-run draw game.",
      url: "https://usalotteryclub.com/results",
      isPartOf: {
        "@type": "WebSite",
        name: "USA Lottery Club",
        url: "https://usalotteryclub.com",
      },
    },
  }),
  component: ResultsPage,
});

function ResultsPage() {
  const { data: results } = useLatestResults(1000);

  const today = new Date();
  const todayIso = today.toISOString().slice(0, 10);
  const todayLabel = today.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  const monthLabel = today.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  // Only show today's results (most recent date in the dataset for each game when ≤ today)
  const todays = (results || []).filter((r: any) => r.draw_date === todayIso);
  const multistate = todays.filter((r: any) => r.games?.is_multistate);
  const stateGames = todays.filter((r: any) => !r.games?.is_multistate);

  // Past-results pills: previous 5 calendar days (excluding today)
  const todayUtcMs = Date.parse(`${todayIso}T00:00:00Z`);
  const pastDays = Array.from({ length: 5 }, (_, i) => {
    const d = new Date(todayUtcMs - (i + 1) * 86400000);
    return {
      iso: d.toISOString().slice(0, 10),
      label: d.toLocaleDateString("en-US", { month: "short", day: "numeric" }),
    };
  });

  const renderRow = (r: any) => {
    const game = r.games;
    const slug = game?.slug;
    const stateAbbr = game?.states?.abbreviation;
    const row = (
      <Card className="transition-colors hover:bg-accent/40">
        <CardContent className="py-3 px-4">
          <div className="flex items-center gap-4">
            {slug && (
              <GameLogo
                gameSlug={slug}
                gameName={game?.name || ""}
                logoUrl={game?.logo_url}
                size="sm"
              />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-2">
                <span className="font-display font-semibold text-sm">{game?.name}</span>
                {stateAbbr && (
                  <span className="text-xs text-muted-foreground">{stateAbbr}</span>
                )}
              </div>
              <NumberRow
                numbers={r.winning_numbers || []}
                bonusNumbers={r.bonus_numbers || []}
                multiplier={r.multiplier}
                size="sm"
                align="left"
              />
            </div>
            <ChevronRight className="h-5 w-5 text-muted-foreground shrink-0" />
          </div>
        </CardContent>
      </Card>
    );
    if (!slug) return <div key={r.id}>{row}</div>;
    return (
      <ResultLink key={r.id} storedSlug={slug} date={r.draw_date}>
        {row}
      </ResultLink>
    );
  };

  return (
    <Layout>
      <SEOHead title="Lottery Results — Today's Winning Numbers" description="View today's US lottery results." />
      <div className="container py-6 md:py-10">
        <DrawJsonLd
          draws={todays.map((r: any) => ({
            gameName: r.games?.name ?? "",
            gameSlug: r.games?.slug ?? "",
            drawDate: r.draw_date,
            winningNumbers: r.winning_numbers ?? [],
            bonusNumbers: r.bonus_numbers ?? [],
            multiplier: r.multiplier ?? null,
            stateName: r.games?.states?.name ?? null,
            url: `https://usalotteryclub.com${resultPathForStoredSlug(r.games?.slug ?? "", r.draw_date)}`,
          }))}
        />
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem><BreadcrumbLink asChild><Link to="/">Home</Link></BreadcrumbLink></BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem><BreadcrumbPage>Results</BreadcrumbPage></BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-2 mb-4">
          <CalendarDays className="h-7 w-7 text-primary" />
          <h1 className="font-sans text-2xl md:text-3xl font-bold">
            US Lottery Results and Winning Numbers
          </h1>
        </div>
        <p className="text-muted-foreground max-w-3xl mb-8">
          Check the latest US lottery winning numbers updated daily. Browse today's results for
          Powerball, Mega Millions, and every state lottery game, or look up past drawings by date.
        </p>

        <h2 className="font-sans font-bold mb-4" style={{ fontSize: "20px" }}>
          Today's Results — {todayLabel}
        </h2>

        {multistate.length > 0 && (
          <>
            <h3 className="font-sans font-semibold text-muted-foreground mb-3" style={{ fontSize: "18px" }}>Multi-State Games</h3>
            <div className="space-y-3 mb-8">{multistate.map(renderRow)}</div>
          </>
        )}

        {stateGames.length > 0 && (
          <>
            <h3 className="font-sans font-semibold text-muted-foreground mb-3" style={{ fontSize: "18px" }}>State Games</h3>
            <div className="space-y-3 mb-8">{stateGames.map(renderRow)}</div>
          </>
        )}

        {todays.length === 0 && (
          <p className="text-muted-foreground mb-8">No results posted yet for today.</p>
        )}

        <h2 className="font-sans font-bold mt-10 mb-4" style={{ fontSize: "20px" }}>Browse Past Results</h2>
        <h3 className="font-sans font-semibold text-muted-foreground mb-3" style={{ fontSize: "16px" }}>{monthLabel}</h3>
        <div className="flex flex-wrap gap-2">
          {pastDays.map((d) => (
            <Link
              key={d.iso}
              to="/results/$date"
              params={{ date: d.iso }}
              className="px-3 py-1.5 rounded-md border bg-card hover:bg-accent transition-colors"
              style={{ fontSize: "14px", color: "rgb(18, 23, 33)" }}
              title={`All US lottery winning numbers drawn on ${d.label}`}
            >
              {d.label}
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}

/** Links a draw row to the canonical result URL for that game. */
function ResultLink({ storedSlug, date, children }: { storedSlug: string; date: string; children: React.ReactNode }) {
  const { gameSlug, stateSlug } = canonicalGameLinkParts(storedSlug);
  const AnyLink = Link as unknown as React.ComponentType<Record<string, unknown>>;
  return (
    <AnyLink to={gamePaths(gameSlug, stateSlug).result(date)} className="block">
      {children}
    </AnyLink>
  );
}
